import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { database, supabase } from '../supabase';
import './Checkout.css';

interface CartItem {
  id: string;
  name: string;
  price: string;
  image: string;
  quantity: number;
  stock?: number;
}

interface CheckoutProps {
  cartItems: CartItem[];
  onBackToCart: () => void;
  onClearCart: () => void;
  onContinueShopping?: () => void;
}

type CheckoutStep = 'cart' | 'shipping' | 'payment' | 'success' | 'auth';

const Checkout: React.FC<CheckoutProps> = ({ cartItems, onBackToCart, onClearCart, onContinueShopping }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('cart');
  const [shippingAddress, setShippingAddress] = useState({
    firstName: '', lastName: '', email: '', phone: '', street: '', houseNumber: '', postalCode: '', city: '', country: 'Netherlands'
  });
  const [orderId, setOrderId] = useState<string | null>(null);
  const orderIdRef = useRef<string | null>(null); // Fix voor closure issue
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmationData, setConfirmationData] = useState<any>(null);
  const [stockIssues, setStockIssues] = useState<CartItem[]>([]);
  const [isCheckingStock, setIsCheckingStock] = useState(false);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const onClearCartRef = useRef(onClearCart);
  onClearCartRef.current = onClearCart;

  useEffect(() => {
    const loadPayPalSDK = () => {
      if ((window as any).paypal) {
        setPaypalLoaded(true);
        return;
      }
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=AfOiAj-DFAmCwZnPK810-8-LvGXXgTG7lJmdYAaLDrpK08PAaVmSZ0jbIMPY_sqP8HAwQo2epx1hwX6T&currency=EUR&intent=capture&locale=nl_NL`;
      script.async = true;
      script.onload = () => setPaypalLoaded(true);
      document.body.appendChild(script);
    };
    const timer = setTimeout(loadPayPalSDK, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true' && currentStep !== 'success') { 
      setCurrentStep('success'); 
      onClearCartRef.current();
    }
  }, [currentStep]);

  const subtotal = cartItems.reduce((total, item) => total + (parseFloat(item.price.replace('$', '').replace(',', '')) * item.quantity), 0);
  const shipping = subtotal > 50 ? 0 : 5.95;
  const tax = subtotal * 0.21;
  const total = subtotal + shipping + tax;

  const validateShipping = () => {
    const newErrors: Record<string, string> = {};
    if (!shippingAddress.firstName) newErrors.firstName = 'Required';
    if (!shippingAddress.lastName) newErrors.lastName = 'Required';
    if (!shippingAddress.email) newErrors.email = 'Required';
    if (!shippingAddress.phone) newErrors.phone = 'Required';
    if (!shippingAddress.street) newErrors.street = 'Required';
    if (!shippingAddress.houseNumber) newErrors.houseNumber = 'Required';
    if (!shippingAddress.postalCode) newErrors.postalCode = 'Required';
    if (!shippingAddress.city) newErrors.city = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStock = async (): Promise<boolean> => {
    setIsCheckingStock(true);
    setStockIssues([]);
    try {
      const productIds = cartItems.map(item => item.id);
      const { data: products } = await supabase.from('products').select('id, stock').in('id', productIds);
      const issues: CartItem[] = [];
      products?.forEach(product => {
        const cartItem = cartItems.find(item => item.id === product.id);
        if (cartItem && product.stock !== null && product.stock < cartItem.quantity) {
          issues.push({ ...cartItem, stock: product.stock });
        }
      });
      if (issues.length > 0) {
        setStockIssues(issues);
        setIsCheckingStock(false);
        return false;
      }
      setIsCheckingStock(false);
      return true;
    } catch {
      setIsCheckingStock(false);
      return true;
    }
  };

  const handleShippingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateShipping()) {
      const stockValid = await validateStock();
      if (!stockValid) {
        setErrors({ stock: 'Some items are out of stock.' });
        return;
      }
      // Directly go to payment if authenticated, otherwise show auth step
      setCurrentStep(isAuthenticated ? 'payment' : 'auth');
    }
  };

  const handleAuthContinue = async () => {
    const stockValid = await validateStock();
    if (!stockValid) {
      setErrors({ stock: 'Some items are out of stock.' });
      return;
    }
    setCurrentStep('payment');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const getEstimatedDelivery = (): string => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 3);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 7);
    const formatDate = (d: Date) => d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
    return `${formatDate(minDate)} - ${formatDate(maxDate)}`;
  };

  const createOrderInSupabase = async (paypalOrderId: string): Promise<string> => {
    const userId = (user as any)?.id || null;
    const orderData = {
      user_id: userId, 
      total_amount: total, 
      status: 'pending', 
      payment_method: 'paypal',
      payment_status: 'pending', 
      paypal_order_id: paypalOrderId, 
      customer_email: shippingAddress.email,
      customer_name: `${shippingAddress.firstName} ${shippingAddress.lastName}`,
      shipping_address: { ...shippingAddress },
      guest_checkout: !isAuthenticated,
      shipping_status: 'pending'
    };
    const { data: order, error } = await database.createOrder(orderData);
    if (error) throw error;
    const orderItems = cartItems.map(item => ({
      order_id: order.id,
      product_id: item.id,
      quantity: item.quantity,
      price: parseFloat(item.price.replace('$', '').replace(',', ''))
    }));
    await supabase.from('order_items').insert(orderItems);
    return order.id;
  };

  const sendOrderEmail = async (orderId: string): Promise<'sent' | 'error'> => {
    setEmailStatus('sending');
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://iradizrwjsbeqlmwejmh.supabase.co';
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
      
      const response = await fetch(`${supabaseUrl}/functions/v1/send-order-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`
        },
        body: JSON.stringify({
          orderId,
          customerEmail: shippingAddress.email,
          customerName: `${shippingAddress.firstName} ${shippingAddress.lastName}`,
          items: cartItems,
          total,
          shippingAddress
        })
      });
      
      const result = await response.json();
      console.log('Email API response:', result);
      
      if (response.ok) {
        setEmailStatus('sent');
        return 'sent';
      } else {
        console.error('Email API error:', result);
        setEmailStatus('error');
        return 'error';
      }
    } catch (error) {
      console.error('Email send error:', error);
      setEmailStatus('error');
      return 'error';
    }
  };

  const renderPayPalButtons = () => {
    if (!paypalLoaded) return <div className="paypal-loading">Loading PayPal...</div>;
    const container = document.getElementById('paypal-buttons-container');
    if (!container) return null;
    container.innerHTML = '';
    if (!(window as any).paypal) return <div className="paypal-error">PayPal SDK failed to load.</div>;
    
    const paypalItems = cartItems.map(item => ({
      sku: item.id,
      quantity: item.quantity.toString(),
      unit_amount: { currency_code: 'EUR', value: parseFloat(item.price.replace('$', '').replace(',', '')).toFixed(2) },
      name: item.name
    }));

    (window as any).paypal.Buttons({
      style: { layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay', height: 45 },
      createOrder: async (data: any, actions: any) => {
        const response = await actions.order.create({
          purchase_units: [{
            reference_id: 'default',
            description: 'ANTI-STYLE HYPOTHESIS Order',
            amount: { currency_code: 'EUR', value: total.toFixed(2), breakdown: { item_total: { currency_code: 'EUR', value: subtotal.toFixed(2) }, shipping: { currency_code: 'EUR', value: shipping.toFixed(2) }, tax_total: { currency_code: 'EUR', value: tax.toFixed(2) } } },
            items: paypalItems,
            shipping: { name: { full_name: `${shippingAddress.firstName} ${shippingAddress.lastName}` }, address: { address_line_1: `${shippingAddress.street} ${shippingAddress.houseNumber}`, admin_area_2: shippingAddress.city, postal_code: shippingAddress.postalCode, country_code: 'NL' } }
          }]
        });
        const newOrderId = await createOrderInSupabase(response);
        setOrderId(newOrderId);
        orderIdRef.current = newOrderId; // Update ref voor closure fix
        return response;
      },
      onApprove: async (data: any, actions: any) => {
        await actions.order.capture();
        const currentOrderId = orderIdRef.current || orderId;
        if (currentOrderId) {
          await supabase.from('orders').update({ payment_status: 'completed', paypal_transaction_id: data.orderID, status: 'confirmed' }).eq('id', currentOrderId);
        }
        
        // Update stock levels - reduce stock for each item in the order
        try {
          for (const item of cartItems) {
            const { data: product, error } = await supabase
              .from('products')
              .select('stock')
              .eq('id', item.id)
              .single();
            
            if (product && !error) {
              const newStock = (product.stock || 0) - item.quantity;
              await supabase
                .from('products')
                .update({ stock: Math.max(0, newStock) })
                .eq('id', item.id);
            }
          }
        } catch (stockError) {
          console.error('Error updating stock:', stockError);
          // Continue with order completion even if stock update fails
        }
        
        // Send email and get final status
        const emailResult = await sendOrderEmail(currentOrderId || '');
        
        const orderConfirmationData = {
          orderId: currentOrderId, items: cartItems, shipping: shippingAddress, subtotal, shippingCost: shipping, tax, total,
          customerEmail: shippingAddress.email,
          customerName: `${shippingAddress.firstName} ${shippingAddress.lastName}`,
          orderDate: new Date().toISOString(),
          estimatedDelivery: getEstimatedDelivery(),
          emailStatus: emailResult
        };
        localStorage.setItem('orderConfirmation', JSON.stringify(orderConfirmationData));
        if (isAuthenticated && user) await database.clearUserCart((user as any).id);
        window.location.href = '/checkout?success=true';
      },
      onCancel: () => setCurrentStep('payment'),
      onError: (err: any) => { console.error('PayPal error:', err); setErrors({ payment: 'Payment failed.' }); }
    }).render('#paypal-buttons-container');
  };

  useEffect(() => {
    if (currentStep === 'payment' && paypalLoaded) {
      renderPayPalButtons();
    }
  }, [currentStep, paypalLoaded]);

  useEffect(() => {
    if (currentStep === 'success') {
      const data = localStorage.getItem('orderConfirmation');
      if (data) {
        setConfirmationData(JSON.parse(data));
      }
    }
  }, [currentStep]);

  if (cartItems.length === 0 && currentStep !== 'success') {
    return (
      <div className="checkout">
        <div className="checkout-container">
          <div className="empty-cart">
            <h1>Your cart is empty</h1>
            <button className="continue-shopping-btn" onClick={() => window.location.hash = '#shop'}>Continue Shopping</button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'success') {
    const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    return (
      <div className="checkout">
        <div className="checkout-container">
          <div className="success-page">
            <div className="success-icon">✓</div>
            <h1>Order Confirmed!</h1>
            <p className="success-subtitle">Thank you for your purchase.</p>
            
            {confirmationData && (
              <div className="order-confirmation-details">
                <div className="confirmation-section">
                  <h3>Order #{confirmationData.orderId?.slice(0, 8).toUpperCase()}</h3>
                  <p className="order-date">{formatDate(confirmationData.orderDate)}</p>
                </div>

                <div className="confirmation-section">
                  <h3>Items</h3>
                  <div className="confirmation-items">
                    {confirmationData.items.map((item: any) => (
                      <div key={item.id} className="confirmation-item">
                        <img src={item.image} alt={item.name} className="confirmation-item-image" />
                        <div className="confirmation-item-details">
                          <h4>{item.name}</h4>
                          <p>Qty: {item.quantity} × €{parseFloat(item.price.replace('$', '').replace(',', '')).toFixed(2)}</p>
                        </div>
                        <div className="confirmation-item-total">
                          €{(parseFloat(item.price.replace('$', '').replace(',', '')) * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="confirmation-section">
                  <h3>Shipping Address</h3>
                  <p>{confirmationData.customerName}</p>
                  <p>{confirmationData.shipping.street} {confirmationData.shipping.houseNumber}</p>
                  <p>{confirmationData.shipping.postalCode} {confirmationData.shipping.city}</p>
                </div>

                <div className="confirmation-section delivery-section">
                  <h3>Estimated Delivery</h3>
                  <p className="delivery-date">{confirmationData.estimatedDelivery}</p>
                </div>

                <div className="confirmation-section">
                  <h3>Order Summary</h3>
                  <div className="confirmation-summary">
                    <div className="summary-row"><span>Subtotal</span><span>€{confirmationData.subtotal.toFixed(2)}</span></div>
                    <div className="summary-row"><span>Shipping</span><span>{confirmationData.shippingCost === 0 ? 'FREE' : `€${confirmationData.shippingCost.toFixed(2)}`}</span></div>
                    <div className="summary-row"><span>Tax (21%)</span><span>€{confirmationData.tax.toFixed(2)}</span></div>
                    <div className="summary-row total"><span>Total</span><span>€{confirmationData.total.toFixed(2)}</span></div>
                  </div>
                </div>

                <div className="email-status-section">
                  <h3>Email Confirmation</h3>
                  {emailStatus === 'sending' && (
                    <div className="email-status sending"><span className="spinner"></span><span>Sending email...</span></div>
                  )}
                  {emailStatus === 'sent' && (
                    <div className="email-status sent"><span className="check-icon">✓</span><span>Confirmation email sent to <strong>{confirmationData.customerEmail}</strong></span></div>
                  )}
                  {emailStatus === 'error' && (
                    <div className="email-status error"><span className="error-icon">!</span><span>Email could not be sent. Please check your inbox.</span></div>
                  )}
                </div>
              </div>
            )}

            <div className="success-actions">
              <button className="continue-shopping-btn" onClick={() => {
                localStorage.removeItem('orderConfirmation');
                // Reset URL to remove success parameter
                if (window.location.search.includes('success=true')) {
                  window.history.replaceState({}, document.title, window.location.pathname);
                }
                if (onContinueShopping) onContinueShopping();
                else window.location.hash = '#archive';
              }}>Continue Shopping</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout">
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Checkout</h1>
          <div className="checkout-steps">
            <div className={`step ${['cart', 'shipping', 'auth', 'payment'].includes(currentStep) ? 'active' : ''}`}><span className="step-number">1</span><span className="step-label">Cart</span></div>
            <div className={`step ${['shipping', 'auth', 'payment'].includes(currentStep) ? 'active' : ''}`}><span className="step-number">2</span><span className="step-label">Shipping</span></div>
            <div className={`step ${['payment'].includes(currentStep) ? 'active' : ''}`}><span className="step-number">3</span><span className="step-label">Payment</span></div>
          </div>
        </div>

        {currentStep === 'cart' && (
          <div className="checkout-step">
            <div className="cart-review">
              <h2>Your Order</h2>
              <div className="cart-items">
                {cartItems.map(item => (
                  <div key={item.id} className="cart-item">
                    <img src={item.image} alt={item.name} className="item-image" />
                    <div className="item-details">
                      <h3>{item.name}</h3>
                      <p>€{parseFloat(item.price.replace('$', '').replace(',', '')).toFixed(2)}</p>
                      {item.stock !== undefined && item.stock < 10 && <p className="stock-warning">Only {item.stock} left!</p>}
                    </div>
                    <div className="item-quantity">Qty: {item.quantity}</div>
                    <div className="item-total">€{(parseFloat(item.price.replace('$', '').replace(',', '')) * item.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="order-summary">
                <div className="summary-row"><span>Subtotal:</span><span>€{subtotal.toFixed(2)}</span></div>
                <div className="summary-row"><span>Shipping:</span><span>{shipping === 0 ? 'FREE' : `€${shipping.toFixed(2)}`}</span></div>
                <div className="summary-row"><span>Tax (21%):</span><span>€{tax.toFixed(2)}</span></div>
                <div className="summary-row total"><span>Total:</span><span>€{total.toFixed(2)}</span></div>
              </div>
              <div className="checkout-actions">
                <button className="back-btn" onClick={onBackToCart}>← Back</button>
                <button className="continue-btn" onClick={() => setCurrentStep('shipping')}>Continue to Shipping</button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 'shipping' && (
          <div className="checkout-step">
            {stockIssues.length > 0 && (
              <div className="stock-issues-container">
                <div className="stock-issues-header"><span className="stock-warning-icon">⚠️</span><h3>Stock Issues Detected</h3></div>
                <p>The following items have insufficient stock:</p>
                <div className="stock-issues-list">
                  {stockIssues.map(item => (
                    <div key={item.id} className="stock-issue-item">
                      <img src={item.image} alt={item.name} className="stock-issue-image" />
                      <div className="stock-issue-details">
                        <h4>{item.name}</h4>
                        <p>Requested: {item.quantity} | Available: {item.stock || 0}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" className="update-cart-btn" onClick={() => window.location.hash = '#shop'}>Update Cart</button>
              </div>
            )}
            {errors.stock && stockIssues.length === 0 && <div className="error-message">{errors.stock}</div>}
            {isCheckingStock && stockIssues.length === 0 && !errors.stock && (
              <div className="stock-checking"><div className="loading-spinner"></div><span>Checking stock availability...</span></div>
            )}
            <form onSubmit={handleShippingSubmit} className="shipping-form">
              <h2>Shipping Information</h2>
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group"><label>First Name *</label><input type="text" name="firstName" value={shippingAddress.firstName} onChange={handleInputChange} className={errors.firstName ? 'error' : ''} /></div>
                  <div className="form-group"><label>Last Name *</label><input type="text" name="lastName" value={shippingAddress.lastName} onChange={handleInputChange} className={errors.lastName ? 'error' : ''} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Email *</label><input type="email" name="email" value={shippingAddress.email} onChange={handleInputChange} className={errors.email ? 'error' : ''} /></div>
                  <div className="form-group"><label>Phone *</label><input type="tel" name="phone" value={shippingAddress.phone} onChange={handleInputChange} className={errors.phone ? 'error' : ''} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Street *</label><input type="text" name="street" value={shippingAddress.street} onChange={handleInputChange} className={errors.street ? 'error' : ''} /></div>
                  <div className="form-group"><label>Number *</label><input type="text" name="houseNumber" value={shippingAddress.houseNumber} onChange={handleInputChange} className={errors.houseNumber ? 'error' : ''} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Postal Code *</label><input type="text" name="postalCode" value={shippingAddress.postalCode} onChange={handleInputChange} className={errors.postalCode ? 'error' : ''} /></div>
                  <div className="form-group"><label>City *</label><input type="text" name="city" value={shippingAddress.city} onChange={handleInputChange} className={errors.city ? 'error' : ''} /></div>
                </div>
              </div>
              <div className="checkout-actions">
                <button type="button" className="back-btn" onClick={() => setCurrentStep('cart')}>← Back</button>
                <button type="submit" className="continue-btn">Continue</button>
              </div>
            </form>
          </div>
        )}

        {currentStep === 'auth' && (
          <div className="checkout-step">
            <h2>Continue as Guest or Create Account</h2>
            <div className="auth-options">
              <div className="auth-option">
                <h3>Guest Checkout</h3>
                <p>Continue without creating an account.</p>
                <button className="continue-btn" onClick={() => handleAuthContinue()}>Continue as Guest</button>
              </div>
              <div className="auth-divider">OR</div>
              <div className="auth-option">
                <h3>Create Account</h3>
                <p>Create an account for faster checkout.</p>
                <button className="back-btn" onClick={() => window.location.hash = '#signup'}>Create Account</button>
              </div>
            </div>
            <div className="checkout-actions">
              <button type="button" className="back-btn" onClick={() => setCurrentStep('shipping')}>← Back</button>
            </div>
          </div>
        )}

        {currentStep === 'payment' && (
          <div className="checkout-step">
            <h2>Payment Method</h2>
            <div className="payment-methods">
              <div className="payment-method">
                <input type="radio" id="paypal" name="paymentMethod" value="paypal" defaultChecked />
                <label htmlFor="paypal"><div className="payment-option"><span className="payment-name">PayPal</span></div></label>
              </div>
            </div>
            {errors.payment && <div className="error-message">{errors.payment}</div>}
            <div id="paypal-buttons-container"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;
